import type { StaticModelIdType } from "@app/types/assistant/models/models";
import type { ReasoningEffortSupport } from "@app/types/assistant/models/types";

// Supported reasoning efforts per static model, mirroring SUPPORTED_MODEL_CONFIGS.
// Kept in sync via tiers.test.ts; literals must stay as const for per-model tier typing.
export const STATIC_MODEL_SUPPORTED_REASONING_EFFORTS = {
  "gpt-3.5-turbo": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-4-turbo": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-4o": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-4.1-2025-04-14": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-4.1-mini-2025-04-14": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-4o-2024-08-06": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-4o-mini": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gpt-5.1": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.2": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.4-mini": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.4": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.5": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.6-sol": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.6-terra": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.6-luna": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-6-sol": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-6-luna": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5.4-nano": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5-mini": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gpt-5-nano": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  o1: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "o1-mini": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  o3: {
    none: false,
    light: false,
    medium: true,
    high: true,
  },
  "o3-mini": {
    none: false,
    light: false,
    medium: true,
    high: true,
  },
  "o4-mini": {
    none: false,
    light: false,
    medium: true,
    high: true,
  },
  "claude-4-opus-20250514": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-4-sonnet-20250514": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-sonnet-4-5-20250929": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-opus-4-5-20251101": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-opus-4-6": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-opus-4-7": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-opus-4-8": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-opus-5-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-opus-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-fable-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-sonnet-5-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-sonnet-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-sonnet-4-6": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-3-opus-20240229": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "claude-3-5-sonnet-20240620": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "claude-3-5-sonnet-20241022": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "claude-3-7-sonnet-20250219": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "claude-3-haiku-20240307": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "claude-3-5-haiku-20241022": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "claude-haiku-5-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "claude-haiku-4-5-20251001": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "mistral-large-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "mistral-medium": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "mistral-medium-3-5": {
    none: true,
    light: false,
    medium: false,
    high: true,
  },
  "mistral-small-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "codestral-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "gemini-2.5-flash": {
    none: true,
    light: true,
    medium: false,
    high: false,
  },
  "gemini-2.5-flash-lite": {
    none: true,
    light: true,
    medium: false,
    high: false,
  },
  "gemini-2.5-pro": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3-pro-preview": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3.1-flash-lite": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3.5-flash-lite": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3.1-flash-lite-preview": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3.1-pro-preview": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3-flash-preview": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3.5-flash": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "gemini-3.6-flash": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "deepseek-chat": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "accounts/fireworks/models/deepseek-v3p2": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "accounts/fireworks/models/deepseek-v4-pro": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "accounts/fireworks/models/deepseek-v4-flash-0731": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "accounts/fireworks/models/kimi-k2-instruct-0905": {
    none: false,
    light: true,
    medium: false,
    high: false,
  },
  "accounts/fireworks/models/kimi-k2p5": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "accounts/fireworks/models/kimi-k2p6": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "accounts/fireworks/models/kimi-k3": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "accounts/fireworks/models/minimax-m2p5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "accounts/fireworks/models/glm-5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "accounts/fireworks/models/glm-5p2": {
    none: false,
    light: false,
    medium: false,
    high: true,
  },
  "grok-3-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "grok-3-mini-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "grok-4.5": {
    none: false,
    light: true,
    medium: true,
    high: true,
  },
  "grok-4-latest": {
    none: true,
    light: true,
    medium: true,
    high: true,
  },
  "grok-4-fast-reasoning-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "grok-4-fast-non-reasoning-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "grok-4-1-fast-non-reasoning-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  "grok-4-1-fast-reasoning-latest": {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  noop: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  auto: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  auto_fast: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  auto_complex: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
} as const satisfies Record<StaticModelIdType, ReasoningEffortSupport>;
